/**
 * activity detail 页面的概要数据的运动总结
 */

import { i18n } from '@client/helper/i18n';
import unit from '@client/helper/unit';
import Definition from '@client/config/definition';
import { cloneDeep } from 'lodash';
import { sportSummaryItems, customSummaryKeyMap } from '@client/pages/activity-detail/config/sport.config';
const { t } = i18n;
import { isNoValue } from '@client/helper/validate';
import { fieldFormatConfig } from './field-format';
import * as corosSportConfig from '@coros/sport-config';
import { EMPTY_TEXT } from '@client/config/other';

import { useActivityDetail } from '../hooks/useActivityDetail';

const { temperatureUnitType } = useActivityDetail();
const { getGradingGrade } = corosSportConfig.sport.climb;
// 公制key
const METRIC_UNITS = Definition.METRIC_UNITS;
// 英制key
const IMPERIAL_UNITS = Definition.IMPERIAL_UNITS;
const NULL_TEXT = '- -';

function getTimeHtml(value, isShowMs) {
  // isShowMs为true，则显示毫秒，否则显示四舍五入后的时间
  const time = isShowMs ? Math.floor(value / 100) : Math.round(value / 100);
  let subTime = value % 100;
  subTime = subTime < 10 ? `0${subTime}` : subTime;

  let result = `<span class="value time">${unit.s2hms(time)}`;

  // 跑步、运动场跑步、骑行、泳池游泳、赛艇，需要毫秒
  if (isShowMs) {
    result += `<span class="unit time">${subTime}</span>`;
  }

  result += `</span>`;

  return result;
}
const isEffectiveNumber = (val) => {
  return !(typeof val !== 'number' || isNaN(val) || val < 0);
};

// 各项概述的定义
// 没有定义key的时候，使用自身的名字作为key
// 一般情况下使用的名字及为后端返回的字段名，但因有多个字段使用相同名字，所以可在内部再定义key
export function getSportSummary({ graphObj, sportName, unitType, summary, isCustomSport }) {
  // 温度单位temperatureUnit特殊逻辑
  const finalTemperatureUnitType = temperatureUnitType.value || 0;

  // 速度配速单位
  function getSpeedUnit() {
    return unit.getSpeedUnitText(summary.speedUnit, unitType);
  }
  const { sportType } = summary;
  const isProgram = !!summary.programId;

  return {
    // 距离(cm)
    Distance: {
      title: t('H1040'),
      valueText: (() => {
        if (isNoValue(summary.distance)) {
          return NULL_TEXT;
        }
        let distance = summary.distance / 100;

        if (!unit.noUserChangeDistance(summary.userChangeDistance)) {
          distance = summary.userChangeDistance / 100;
        }

        const distanceInfo = unit.getDistanceInfo(sportType, distance, unitType);

        return `${Number(distanceInfo.value).toFixed(2)}`;
      })(),
      unit: (() => {
        let distance = summary.distance / 100;

        if (!unit.noUserChangeDistance(summary.userChangeDistance)) {
          distance = summary.userChangeDistance / 100;
        }

        const distanceInfo = unit.getDistanceInfo(sportType, distance, unitType);

        return `${distanceInfo.unit}`;
      })()
    },
    // 滑降距离(cm)
    DownhillDist: {
      title: t('H1049'),
      valueText: (() => {
        if (isNoValue(summary.distance)) {
          return NULL_TEXT;
        }
        const distance = summary.distance / 100;

        return {
          [METRIC_UNITS]: unit.distanceM2Km,
          [IMPERIAL_UNITS]: unit.distanceM2Mile
        }[unitType](distance);
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'km',
          [IMPERIAL_UNITS]: 'mi'
        }[unitType];
      })()
    },
    // 竞速帆板最快2s
    Max2s: {
      title: t('H4008'),
      valueText: (() => {
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, summary.max2s, 2, unitType);
      })(),
      unit: (() => {
        return getSpeedUnit();
      })()
    },
    // 竞速帆板平均5x10s
    Avg5x10s: {
      title: t('H4009'),
      valueText: (() => {
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, summary.avg5x10s, 2, unitType);
      })(),
      unit: (() => {
        return getSpeedUnit();
      })()
    },
    // 竞速帆板滑行距离
    TotalRunsDistance: {
      title: t('H1059'),
      valueText: (() => {
        if (isNoValue(summary.distance)) {
          return NULL_TEXT;
        }
        const distance = summary.distance / 100;

        return {
          [METRIC_UNITS]: unit.distanceM2Km,
          [IMPERIAL_UNITS]: unit.distanceM2Mile
        }[unitType](distance);
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'km',
          [IMPERIAL_UNITS]: 'mi'
        }[unitType];
      })()
    },
    // 组数
    Sets: {
      title: t('H1051'),
      valueText: (() => {
        const { sets } = summary;
        if (isNoValue(sets)) {
          return NULL_TEXT;
        }
        return sets;
      })(),
      unit: (() => {
        const { sets } = summary;
        if (isNoValue(sets)) {
          return '';
        }
        return t('H8004', { OvO: '' });
      })()
    },
    // 游泳/滑雪趟数
    Lengths: {
      title: t('H1052'),
      valueText: summary.lengths
    },
    // 竞速帆板趟数
    Runs: {
      title: t('H1063'),
      valueText: summary.lengths
    },
    // 运动时间(*100s)
    WorkoutTime: {
      title: t('H1041'),
      html: getTimeHtml(summary.workoutTime, summary.isShowMs)
    },
    // 滑降时间(s)
    DownhillTime: {
      title: t('H1053'),
      html: getTimeHtml(summary.downhillTime, summary.isShowMs)
    },
    // 竞速帆板滑行时间
    TotalRunsTime: {
      title: t('H1060'),
      html: getTimeHtml(summary.workoutTime, summary.isShowMs)
    },
    // 总时间(s)
    TotalTime: {
      title: t('H1042'),
      html: getTimeHtml(summary.totalTime, summary.isShowMs)
    },
    // 平均配速(单位见speed_unit)
    AvgPace: {
      title: t('H1054'),
      valueText: (() => {
        const { avgPace, avgSpeed } = summary;
        let { speedUnit } = summary;
        let value = avgSpeed;
        if (isCustomSport && isCustomSport.value) {
          value = avgPace;
          speedUnit = 3;
        }
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(speedUnit, value, 2, unitType);
      })(),
      unit: (() => {
        if (isCustomSport && isCustomSport.value) {
          const speedUnit = 3;
          return unit.getSpeedUnitText(speedUnit, unitType);
        }
        return getSpeedUnit();
      })()
    },
    // 平均速度(单位见speed_unit)
    AvgSpeed: {
      title: t('H1020'),
      valueText: (() => {
        if (['Jump_Rope'].includes(sportName)) {
          const value = summary.ropeAvgSpeed;
          if (!isEffectiveNumber(value)) {
            return NULL_TEXT;
          }
          return value;
        }

        const { avgSpeed } = summary;
        let { speedUnit } = summary;
        const value = avgSpeed;
        if (!value) {
          return NULL_TEXT;
        }
        if (isCustomSport && isCustomSport.value) {
          speedUnit = 1;
        }
        // 除竞速帆板运动外，其他运动类型的速度由两位小数改为一位小数
        // 竞速帆板运动没有 AvgSpeed 字段
        return unit.getSpeedValueWithSpeedType(speedUnit, value, 1, unitType);
      })(),
      unit: (() => {
        if (['Jump_Rope'].includes(sportName)) {
          return 'reps/min';
        }
        if (isCustomSport && isCustomSport.value) {
          const speedUnit = 1;
          return unit.getSpeedUnitText(speedUnit, unitType);
        }
        return getSpeedUnit();
      })()
    },
    // 最快1公里(单位见speed_unit)
    Bestkm: {
      title: {
        [METRIC_UNITS]: t('H1022'),
        [IMPERIAL_UNITS]: t('H1073')
      }[unitType],
      valueText: (() => {
        const value = summary.bestKm;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 1, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 最快500m
    Best500m: {
      title: t('H1061'),
      valueText: (() => {
        const value = summary.bestKm;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 1, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 最快速度(单位见speed_unit)
    MaxSpeed: {
      title: t('H1023'),
      valueText: (() => {
        if (['Jump_Rope'].includes(sportName)) {
          const value = summary.ropeMaxSpeed;
          if (!isEffectiveNumber(value)) {
            return NULL_TEXT;
          }
          return value;
        }

        const value = summary.maxSpeed;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 1, unitType);
      })(),
      unit: (() => {
        if (['Jump_Rope'].includes(sportName)) {
          return 'reps/min';
        }

        return getSpeedUnit();
      })()
    },
    // 最快单趟(单位见speed_unit)
    BestLen: {
      title: t('H1024'),
      valueText: (() => {
        const value = summary.bestLength;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 1, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 竞速帆板最快100m
    Fastest100m: {
      title: t('H4010'),
      valueText: (() => {
        const value = summary.fastest100m;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 竞速帆板最快250m
    Fastest250m: {
      title: t('H4011'),
      valueText: (() => {
        const value = summary.fastest250m;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 竞速帆板最快500m
    Fastest500m: {
      title: t('H1061'),
      valueText: (() => {
        const value = summary.fastest500m;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 竞速帆板最快1852m
    Fastest1852m: {
      title: t('H4012'),
      valueText: (() => {
        const value = summary.fastest1852m;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 竞速帆板Alpha500
    Alpha500: {
      title: t('H4013'),
      valueText: (() => {
        const value = summary.alpha500;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 平均心率
    AvgHR: {
      title: t('H1055'),
      valueText: (() => {
        const value = summary.avgHr;
        if ([undefined, 'undefined', null, '', 0, '0'].includes(value)) {
          return '- -';
        }
        return value;
      })(),
      unit: 'bpm'
    },
    // 平均功率
    AvgPower: {
      title: t('H1025'),
      valueText: (() => {
        const value = summary.avgPower;
        return value;
      })(),
      unit: 'w'
    },
    // NP
    NP: {
      title: t('H1027'),
      valueText: (() => {
        const value = summary.np;
        return value;
      })(),
      unit: 'w'
    },
    // 骑行大类通用
    Bike_IF: {
      title: t('H9090'), // Intensity Factor
      valueText: (() => {
        const val = summary.bicycleIf / 100;
        if (val === 0) return val;
        return val.toFixed(2);
      })()
    },
    // 姿势功率比
    FPRatio: {
      title: t('H1028'),
      valueText: (() => {
        const value = summary.fpRatio;
        return value;
      })(),
      unit: '%',
      useIt() {
        return !!summary.fpRatio;
      }
    },
    // 平均划次
    AverageStroke: {
      title: t('S4692'),
      valueText: (() => {
        if (!summary.lengths) return EMPTY_TEXT;
        // 单位空，不区分公英制，保留整数
        const value = (summary.totalCadence / summary.lengths).toFixed(0);
        return value;
      })()
    },
    // SWOLF
    SWOLF: {
      title: t('H1029'),
      valueText: (() => {
        const value = summary.avgSwolfLen;
        return value;
      })()
    },
    // 平均步频
    AvgCadence: {
      title: t('H1030'),
      valueText: (() => {
        const value = summary.avgCadence;
        return value;
      })()
    },
    AvgGrade: {
      title: t('H9045'),
      valueText: (() => {
        const value = summary.avgGrade;
        return value;
      })(),
      unit: '%'
    },
    // 平均划频
    AvgStrkRate: {
      title: t('H1033'),
      valueText: (() => {
        const value = summary.avgCadence;
        return value;
      })(),
      unit: 'strokes/min'
    },
    // 平均桨频
    AvgPaddleRate: {
      title: t('H1062'),
      valueText: (() => {
        const value = summary.avgCadence;
        return value;
      })()
    },
    // 累计上升(m)
    ElevGain: {
      title: t('H1034'),
      valueText: (() => {
        const value = summary.elevGain;
        const result = {
          [METRIC_UNITS]: (value) => {
            return Math.round(value);
          },
          [IMPERIAL_UNITS]: (value) => {
            return Math.round(unit.distanceM2ft(value));
          }
        }[unitType](value);
        return result;
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'm',
          [IMPERIAL_UNITS]: 'ft'
        }[unitType];
      })()
    },
    // 累计下降(m)
    TotalDescent: {
      title: t('H1035'),
      valueText: (() => {
        const value = summary.totalDescent;
        const result = {
          [METRIC_UNITS]: (value) => {
            return Math.round(value);
          },
          [IMPERIAL_UNITS]: (value) => {
            return Math.round(unit.distanceM2ft(value));
          }
        }[unitType](value);
        return result;
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'm',
          [IMPERIAL_UNITS]: 'ft'
        }[unitType];
      })()
    },
    // 滑降下降(m)
    DownhillDesc: {
      title: t('H1036'),
      valueText: (() => {
        const value = summary.downhillDesc;
        const result = {
          [METRIC_UNITS]: (value) => {
            return value;
          },
          [IMPERIAL_UNITS]: unit.distanceM2ft
        }[unitType](value);
        return result;
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'm',
          [IMPERIAL_UNITS]: 'ft'
        }[unitType];
      })()
    },
    // 最大坡度(%)
    MaxGrade: {
      title: t('H1037'),
      valueText: (() => {
        const value = summary.maxGrade;
        return value;
      })(),
      unit: '%'
    },
    // 训练负荷
    TrainingLoad: {
      title: t('H1056'),
      valueText: (() => {
        const value = summary.trainingLoad;
        return value;
      })()
    },
    // 卡路里(kal)
    Calories: {
      title: t('H1038'),
      valueText: (() => {
        const value = summary.calories;
        return (value / 1000).toFixed(0);
      })(),
      unit: 'kcal'
    },
    // 训练效果
    Estimation: {
      title: t('H1101'),
      valueText: (() => {
        const value = summary.estimation;
        return value;
      })()
    },
    // 绳距
    Pitches: {
      title: t('H5043'),
      valueText: (() => {
        const value = summary.pitch;
        return value;
      })()
    },
    // 垂直速度
    Vert_Speed: {
      title: t('H1021'),
      valueText: (() => {
        const value = summary.vertSpeed;
        if (isNoValue(value)) {
          return NULL_TEXT;
        }
        return {
          [METRIC_UNITS]: (value) => {
            return value;
          },
          [IMPERIAL_UNITS]: unit.distanceM2ft
        }[unitType](value);
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'm/h',
          [IMPERIAL_UNITS]: 'ft/h'
        }[unitType];
      })()
    },
    // 最高难度
    Hardest_Grade: {
      title: t('H5045'),
      valueText: (() => {
        /*
            |-- gradingSystem|32位int|难度系统|0：不设置<br/>1：优胜美地<br/>2：法式<br/>3：器械攀岩<br/>4：攀冰<br/>5：冰岩混合|
            |-- hardestGrade|32位int|单圈最大难度||
            * */
        const flag = ['IndoorClimb', 'Bouldering'].includes(sportName);
        const gradeInfo = getGradingGrade(flag, summary.gradingSystem, summary.hardestGrade);

        if (!gradeInfo) {
          return NULL_TEXT;
        }

        return gradeInfo.gradeNameI18n ? t(gradeInfo.gradeNameI18n) : gradeInfo.gradeName;
      })()
    },
    // 步数
    Steps: {
      title: t('H5044'),
      valueText: (() => {
        const value = summary.totalCadence;
        return value;
      })()
    },
    // 体温
    Body_Temperature: {
      title: t('H9023'),
      valueText: (() => {
        const value = summary.bodyTemperature;
        if (!value) {
          return NULL_TEXT;
        }

        return {
          [METRIC_UNITS]: (val, fix) => {
            return Number(val.toFixed(fix));
          },
          [IMPERIAL_UNITS]: unit.temperatureC2F
        }[finalTemperatureUnitType](value / 100, 2);
      })(),
      unit: corosSportConfig.basic.temperature.getUnit(finalTemperatureUnitType),
      useIt() {
        return Reflect.has(graphObj, 'bodyTemperature') && !!summary.bodyTemperature;
      }
    },
    // 水温
    Water_Temperature: {
      title: t('H9022'),
      valueText: (() => {
        const value = summary.waterTemperature;
        if (!value) {
          return NULL_TEXT;
        }

        return {
          [METRIC_UNITS]: (val, fix) => {
            return Number(val.toFixed(fix));
          },
          [IMPERIAL_UNITS]: unit.temperatureC2F
        }[finalTemperatureUnitType](value / 100, 0);
      })(),
      unit: corosSportConfig.basic.temperature.getUnit(finalTemperatureUnitType),
      useIt() {
        return Reflect.has(graphObj, 'waterTemperature');
      }
    },
    // 等价配速
    Adjusted_Pace: {
      title: t('H9024'),
      valueText: (() => {
        let { speedUnit, adjustedPace } = summary;

        if (!['Run', 'Trail_Run', 'Custom_Sport'].includes(sportName)) {
          return NULL_TEXT;
        }
        if (sportName === 'Custom_Sport') {
          speedUnit = 3;
        }
        if (!adjustedPace) {
          return NULL_TEXT;
        }

        return unit.getSpeedValueWithSpeedType(speedUnit, adjustedPace, 2, unitType);
      })(),
      unit: (() => {
        let { speedUnit } = summary;
        if (sportName === 'Custom_Sport') {
          speedUnit = 3;
        }
        return unit.getSpeedUnitText(speedUnit, unitType);
      })()
    },
    // 等价速度
    Adjusted_Speed: {
      title: t('H9025'),
      valueText: (() => {
        const value = summary.adjustedPace;

        if (!['XC_Ski', 'Ski_Touring', 'Custom_Sport'].includes(sportName)) {
          return NULL_TEXT;
        }

        if (!value) {
          return NULL_TEXT;
        }

        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })(),
      unit: getSpeedUnit()
    },
    // 达标率
    StandardRate: {
      title: t('TD1065'),
      valueText: (() => {
        const value = summary.standardRate;
        if (!isEffectiveNumber(value)) {
          return NULL_TEXT;
        }

        return (value * 100).toFixed(1);
      })(),
      unit: '%',
      useIt() {
        return isProgram;
      }
    },
    // 次数
    Reps: {
      title: t('H6014'),
      valueText: (() => {
        if (['Jump_Rope'].includes(sportName)) {
          const value = summary.totalReps;
          if (!isEffectiveNumber(value)) {
            return NULL_TEXT;
          }
          return value;
        }

        const value = summary.reps;
        if (!isEffectiveNumber(value)) {
          return NULL_TEXT;
        }

        return (value * 100).toFixed(1);
      })()
    },
    // 最多连跳
    Max_Cont_Jumps: {
      title: t('H6014'),
      valueText: (() => {
        if (['Jump_Rope'].includes(sportName)) {
          const value = summary.maxJump;
          if (!isEffectiveNumber(value)) {
            return NULL_TEXT;
          }
          return value;
        }

        const value = summary.maxContJumps;
        if (!isEffectiveNumber(value)) {
          return NULL_TEXT;
        }

        return (value * 100).toFixed(1);
      })(),
      unit: '%',
      useIt() {
        return isProgram;
      }
    },

    // 3.0 追加
    // 乳酸阈配速
    TestThresholdPace: {
      title: t('Y9002'),
      unit: getSpeedUnit(),
      valueText: (() => {
        const value = summary.testThresholdPace;
        if (!value) {
          return NULL_TEXT;
        }
        return unit.getSpeedValueWithSpeedType(summary.speedUnit, value, 2, unitType);
      })()
    },
    // 乳酸阈心率
    TestThresholdHr: {
      title: t('Y8002'),
      unit: 'bpm',
      valueText: (() => {
        const value = summary.testThresholdHr;
        if ([undefined, 'undefined', null, '', 0, '0'].includes(value)) {
          return '- -';
        }
        return value;
      })()
    },
    // 个人最大心率
    TestMaxHr: {
      title: t('H9040'),
      unit: 'bpm',
      valueText: (() => {
        const value = summary.testMaxHr;
        if ([undefined, 'undefined', null, '', 0, '0'].includes(value)) {
          return '- -';
        }
        return value;
      })()
    },
    // TODO: todo_xjf Falls, ClimbingTime, MaxActivityFall 三个未完成
    // 坠落次数（Falls）
    Falls: {
      title: t('H9033'),
      valueText: (() => {
        const value = summary.falls;
        if ([undefined, 'undefined', null, ''].includes(value)) {
          return '- -';
        }
        return value;
      })()
    },
    // 上升时间（Climbing Time）
    ClimbingTime: {
      title: t('H9037'),
      html: getTimeHtml(summary.climbingTime, summary.isShowMs)
    },
    // 最大冲坠（Max Activity Fall）
    MaxActivityFall: {
      title: t('H9034'),
      valueText: (() => {
        if ([undefined, 'undefined', null, ''].includes(summary.maxActivityFall)) {
          return '- -';
        }
        let distance = summary.maxActivityFall / 100;
        return fieldFormatConfig.MaxActivityFall.getValue(distance, unitType);
      })(),
      unit: fieldFormatConfig.MaxActivityFall.getUnit(unitType)
    },
    // 路线
    Routes: {
      title: sportType === 801 ? t('H9095') : t('H9039'),
      unit: sportType === 801 ? t('H9095') : t('H9039'),
      valueText: (() => {
        const value = summary.pitch;
        if (!value) {
          return NULL_TEXT;
        }
        return value;
      })()
    },
    // 总上升（Total Ascent）
    TotalAscent: {
      title: t('H9035'),
      valueText: (() => {
        const value = summary.elevGain;
        const result = {
          [METRIC_UNITS]: (value) => {
            return value.toFixed(1);
          },
          [IMPERIAL_UNITS]: (value) => {
            return unit.distanceM2ft(value, 1);
          }
        }[unitType](value / 10);
        return result;
      })(),
      unit: (() => {
        return {
          [METRIC_UNITS]: 'm',
          [IMPERIAL_UNITS]: 'ft'
        }[unitType];
      })()
    },
    // 左右踩踏平滑度
    LeftRightPedalSmoothness: {
      title: t('H9093'),
      html: `<div class="value"><span>${summary.leftPedalSmoothness}</span><span class="percentUnit"> %</span> - <span>${summary.rightPedalSmoothness}</span><span class="percentUnit"> %</span><div>`,
      useIt() {
        return !!summary.leftPedalSmoothness && !!summary.rightPedalSmoothness;
      }
    },
    // 左右扭矩效率
    LeftRightTorqueEffectiveness: {
      title: t('H9094'),
      html: `<div class="value"><span>${summary.leftTorqueEffectiveness}</span><span class="percentUnit"> %</span> - <span>${summary.rightTorqueEffectiveness}</span><span class="percentUnit"> %</span><div>`,
      useIt() {
        return !!summary.leftTorqueEffectiveness && !!summary.rightTorqueEffectiveness;
      }
    },
    //骑行左右平衡
    BikeLeftRightBalance: {
      title: t('H9096'),
      html: `<div class="value"><span>${summary.leftBalance}</span><span class="percentUnit"> %</span> - <span>${summary.rightBalance}</span><span class="percentUnit"> %</span><div>`,
      useIt() {
        return !!summary.leftBalance && !!summary.rightBalance;
      }
    },
    // 站姿时间
    StandingTime: {
      title: t('H9098'),
      valueText: (() => {
        const { standingTime = 0 } = summary;
        const result = unit.s2hmsStr(Math.round(standingTime));
        return result;
      })(),
      useIt() {
        return !!summary.standingTime;
      }
    },
    // 坐姿时间
    SeatedTime: {
      title: t('H9097'),
      valueText: (() => {
        const { seatedTime = 0 } = summary;
        const result = unit.s2hmsStr(Math.round(seatedTime));
        return result;
      })(),
      useIt() {
        return !!summary.seatedTime;
      }
    },
    // 爬楼层数 Climb_Stairs 902
    Climb_Stairs_Floors: {
      title: t('H9109'),
      valueText: (() => {
        const value = summary.floors;
        return value || EMPTY_TEXT;
      })(),
      unit: (() => {
        if (!summary.floors) {
          return '';
        }
        return t('H9110');
      })()
    }
  };
}

// 定义每项运动分别有哪些概要字段显示
export function getSportSummaryItems(isCustomSport, topSummary) {
  const summaryItems = cloneDeep(sportSummaryItems);
  let customKeys = [];
  if (isCustomSport) {
    customKeys = getCustomSportKeys(topSummary);
  }
  summaryItems.Custom_Sport = customKeys;
  return summaryItems;
}
export function getCustomSportKeys(topSummary) {
  const { customDataTypeList = [], customDataDsMultiplexList } = topSummary;

  const arr = [];
  // 筛选出支持的dataType
  customDataTypeList.forEach((dataType) => {
    // console.log(`🔔 customSummaryKeyMap`, customSummaryKeyMap);
    const child = customSummaryKeyMap[dataType];
    if (child) {
      child.forEach((item) => {
        const { dsMultiplex } = item;
        if (dsMultiplex) {
          // 匹配复合条件
          if (customDataDsMultiplexList && customDataDsMultiplexList.includes(dsMultiplex)) arr.push(item);
        } else {
          arr.push(item);
        }
      });
    }
  });
  // 根据order排序
  arr.sort((a, b) => {
    return a.order - b.order;
  });
  // 提取数据
  let keys = [];
  arr.forEach((item) => {
    item.keys.forEach((key) => {
      keys.push({ key });
    });
  });
  keys = Array.from(new Set(keys));

  return keys;
}
